import Popover from "@mui/material/Popover";
import { useCreateAdditionalAuction } from "apollo/hooks/mutations";
import { useGetAvailableSearchTypes } from "apollo/hooks/queries";
import { GET_PATIENT } from "core/apollo/graphql";
import { QUERY_PROGRESS_SUCCEED } from "core/consts";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { capitaliseFirst } from "core/model/utils/strings";
import { Patient, SearchType } from "core/types";
import RSButton from "ds_legacy/components/RSButton";
import { padding, sizing } from "ds_legacy/materials/metrics";
import { MenuItem } from "dsl/atoms/MenuItem";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useCareseekerNavigationHandlers, useDropdown } from "dsl/hooks";
import { PRODUCT_TOURS, tourAttributes } from "dsl/molecules/useProductTour";
import { useParallelSearchTour } from "dsl/molecules/useProductTour/tours/parallelSearchTour";
import { PlusIcon } from "lucide-react";
import { useTranslations } from "translations";

const useGetAdditionalSearchOptions = ({ patient }: { patient: Patient }) => {
  const getOntology = useGetOntology();
  const [availableSearchTypes, getAvailableSearchTypesQueryProgress] =
    useGetAvailableSearchTypes({
      patientId: patient.id,
    });

  const additionalSearchOptions = availableSearchTypes.map((value) => {
    return {
      label: getOntology({
        key: value,
        type: "patientType",
      }),
      value: value as SearchType,
    };
  });

  return [
    additionalSearchOptions,
    getAvailableSearchTypesQueryProgress,
  ] as const;
};

const useHandleAuctionCreation = ({ patient }: { patient: Patient }) => {
  const appNavigation = useCareseekerNavigationHandlers();
  const toast = useToast();
  const translations = useTranslations();
  const [createAdditionalAuction, createAdditionalAuctionQueryProgress] =
    useCreateAdditionalAuction();

  const handleAuctionCreation = (element: {
    label: string;
    value: SearchType;
  }) => {
    createAdditionalAuction({
      onError: (error) => {
        console.error("error creating additional auction:", error);
        toast({
          color: "danger",
          message:
            translations.careproviderApp.settings.reversePatientSearch
              .toastError,
        });
      },
      update: (cache, { data }) => {
        if (!data?.auction) {
          console.error("no auction returned");
          return;
        }

        const patientWithNewAuction = {
          ...patient,
          auctions: [...(patient.auctions || []), data.auction],
        };

        cache.writeQuery({
          data: {
            patient: patientWithNewAuction,
          },
          query: GET_PATIENT,
          variables: { id: patient.id },
        });

        appNavigation.patient.goToAssessing({
          auctionId: data.auction.id,
          patientId: patient.id,
        });
      },
      variables: {
        input: {
          search_type: element.value,
        },
        patientId: patient.id,
      },
    });
  };

  return [handleAuctionCreation, createAdditionalAuctionQueryProgress] as const;
};

const AddSearchDropdown = ({ patient }: { patient: Patient }) => {
  const { anchor, onClose, onOpen, open } = useDropdown();
  const translations = useTranslations();

  const [availableSearchTypes, getAvailableSearchTypesQueryProgress] =
    useGetAdditionalSearchOptions({
      patient: patient,
    });

  const [handleCreateAdditionalAuction, createAdditionalAuctionQueryProgress] =
    useHandleAuctionCreation({ patient: patient });

  useParallelSearchTour({
    hasParallelSearchOption:
      !!availableSearchTypes?.length &&
      getAvailableSearchTypesQueryProgress === QUERY_PROGRESS_SUCCEED,
  });

  if (
    !availableSearchTypes?.length &&
    getAvailableSearchTypesQueryProgress === QUERY_PROGRESS_SUCCEED
  )
    return null;

  return (
    <div
      {...tourAttributes({
        stepKey: PRODUCT_TOURS.parallel_search.steps.new_search_tab.key,
        tourKey: PRODUCT_TOURS.parallel_search.key,
      })}
    >
      <RSButton
        color="primary"
        id="add_search"
        LeftIcon={PlusIcon}
        loading={createAdditionalAuctionQueryProgress}
        style={{
          whiteSpace: "nowrap",
        }}
        variant="text"
        onClick={onOpen}
      >
        {translations.actions.addSearch}
      </RSButton>

      <Popover
        anchorEl={anchor}
        anchorOrigin={{
          horizontal: "left",
          vertical: "bottom",
        }}
        open={open}
        transformOrigin={{
          horizontal: "left",
          vertical: "top",
        }}
        onClose={onClose}
      >
        {availableSearchTypes.map(
          (element: { label: string; value: SearchType }) => (
            <MenuItem
              key={element.value}
              data-testid={`add_search_${element.value}`}
              sx={{
                display: "flex",
                justifyContent: "center",
                minWidth: sizing(16),
                padding: padding(1),
                whiteSpace: "nowrap",
              }}
              onClick={() => handleCreateAdditionalAuction(element)}
            >
              {capitaliseFirst(element.label.toLowerCase())}
            </MenuItem>
          ),
        )}
      </Popover>
    </div>
  );
};

export default AddSearchDropdown;
